import { observer } from 'mobx-react-lite'
import { useLocation, useParams } from 'react-router'
import React, { useState } from 'react'
import { Box, Button, Divider, Dropdown, IconButton, Tooltip } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { layoutStore } from 'shared/layout'
import { ChatbotRoutes } from 'entities/ExportRouters'
import { ChatbotInstructionType, ChatbotStatus } from 'entities/Chatbot'
import { ChatbotStatusBadge } from 'pages/chatbot/ui'
import { useChatbotContext, useChatbotDialogContext } from 'pages/chatbot/context'
import { TextingTemplateMap } from 'pages/chatbot/config'
import { AgentEditCustomTitle } from 'pages/chatbot/ui/AgentEditCustomTitle/AgentEditCustomTitle'
import { AgentEditHeader } from 'pages/chatbot/ui/AgentEditHeader/AgentEditHeader'
import { AgentTypeList } from 'pages/chatbot/ui/AgentTypeList/AgentTypeList'

export const ChatbotEditHead = observer(() => {
  const {
    model,
    isDisabledSave,
    loaded,
    activeTemplates,
    instructions,
    id,
    isSaving,
    disableChatbot,
    handleSaveAgent,
    handlePublishAgent,
    handleTestNewAgent,
    updateChatbot,
    locked,
    dirty,
    errors,
  } = useChatbotContext()
  const { isActive, firstMessage, status, type, name, setName } = model
  const template = TextingTemplateMap.get(type ?? ChatbotInstructionType.BookAppointment)
  const nameError = errors.getError('name')
  const { state: payload } = useLocation()

  const handleNameChange = (value: string) => {
    setName(value)
    errors.removeError('name')
  }
  const { invalidate, loading: dialogLoading, start } = useChatbotDialogContext()
  const { agentId } = useParams()
  const [showAgentTypeSelector, setShowAgentTypeSelector] = useState(false)
  const [updating, setUpdating] = useState(false)
  const { isViewXs } = layoutStore
  const isTestButtonDisabled = agentId
    ? dialogLoading || locked || updating
    : locked || !dirty || updating

  if (agentId && !loaded) return null

  const handleTestAgent = async () => {
    if (agentId) {
      if (dirty) {
        setUpdating(true)
        const updated = await updateChatbot(true)
        setUpdating(false)

        if (!updated) return
      }
      start(firstMessage.isAutoConvType)
    } else {
      await handleTestNewAgent()
    }
  }

  const renderActions = () => [
    {
      iconButtonComponent: (
        <Box display='flex' alignItems='center' gap='8px'>
          <Dropdown
            width={showAgentTypeSelector ? 330 : 162}
            placement='bottom-start'
            icon={'moreHorizontal'}
            tooltipProps={{
              label: 'Show actions',
              placement: 'top-end',
            }}
            iconButtonProps={{
              fontSize: 20,
              variant: 'contained',
              size: 'medium',
              ariaLabel: 'Actions_showActions',
            }}
            onChangeOpen={(isOpen) => {
              if (!isOpen) {
                setTimeout(() => setShowAgentTypeSelector(false), 100)
              }
            }}
            onChange={(item) => {
              if (item.id === 2) {
                setShowAgentTypeSelector(true)
              }
            }}
            items={
              showAgentTypeSelector
                ? []
                : [
                    ...(isActive
                      ? [
                          {
                            id: 1,
                            label: 'Disable agent',
                            onClick: disableChatbot,
                          },
                        ]
                      : []),
                    ...(isViewXs
                      ? [
                          {
                            id: 3,
                            label: 'Test agent',
                            onClick: handleTestAgent,
                            disabled: isTestButtonDisabled,
                          },
                        ]
                      : []),
                    {
                      id: 2,
                      label: 'Change agent type',
                      disabledClosedTooltip: true,
                    },
                  ]
            }
            customComponent={
              showAgentTypeSelector
                ? (closeMenu) => (
                    <AgentTypeList
                      activeTemplates={activeTemplates}
                      model={model}
                      id={id}
                      instructions={instructions}
                      closeMenu={closeMenu}
                    />
                  )
                : undefined
            }
          />
          {!isActive && (
            <Button
              text={agentId ? 'Save changes' : 'Save as draft'}
              contained='secondary'
              onClick={() => handleSaveAgent(invalidate)}
              disabled={isDisabledSave || isSaving}
            />
          )}
          <Button
            text={isActive ? 'Publish changes' : 'Activate'}
            contained='primary'
            onClick={handlePublishAgent}
            disabled={isSaving}
          />
          <Box display='flex' alignItems='center'>
            <Divider
              orientation='vertical'
              sx={{ height: '16px !important', marginRight: '8px' }}
            />
            <IconButton
              ariaLabel='close_setting'
              icon='close'
              variant='contained'
              color='tertiary'
              size='medium'
              onClick={() =>
                uiStore.changeRoute({
                  path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}`,
                })
              }
            />
          </Box>
        </Box>
      ),
    },
  ]

  return (
    <AgentEditHeader
      customTitle={
        <AgentEditCustomTitle
          template={template}
          name={name}
          setName={handleNameChange}
          error={nameError}
          isAutoFocus={payload?.duplicateId}
        />
      }
      rightTitleContent={
        status !== null ? (
          <Tooltip
            label={status === ChatbotStatus.Invalid && 'Suspicious content detected'}
            placement='top'
          >
            <ChatbotStatusBadge status={status} />
          </Tooltip>
        ) : null
      }
      actionsGap={8}
      actions={renderActions()}
    />
  )
})
